/**
 * OnboardingContext — single source of truth for the wizard state in the UI.
 *
 * - Fetches /api/onboarding/status on mount (after auth).
 * - Exposes complete(stepId) / skip(stepId) / mark(eventType) / reset()
 *   helpers that hit the backend and locally mirror the resulting state.
 * - Skipped if the caller is an owner (no org) or unauthenticated.
 */
import React, { createContext, useContext, useEffect, useCallback, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [steps, setSteps] = useState([]);
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isEligible = !!user && user.role !== "owner";

  const refresh = useCallback(async () => {
    if (!isEligible) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/onboarding/status");
      setState(data.state);
      setSteps(data.steps || []);
      setAccountType(data.account_type);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load onboarding");
    } finally {
      setLoading(false);
    }
  }, [isEligible]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const complete = useCallback(async (stepId) => {
    const { data } = await api.post(`/onboarding/complete/${stepId}`);
    setState(data.state);
    return data.state;
  }, []);

  const skip = useCallback(async (stepId) => {
    const { data } = await api.post(`/onboarding/skip/${stepId}`);
    setState(data.state);
    return data.state;
  }, []);

  const mark = useCallback(async (eventType) => {
    try {
      const { data } = await api.post(`/onboarding/mark/${eventType}`);
      setState(data.state);
      return data.state;
    } catch (e) {
      // Marking events is best-effort — don't disrupt the user.
      return null;
    }
  }, []);

  const reset = useCallback(async () => {
    const { data } = await api.post(`/onboarding/reset`);
    setState(data.state);
    return data.state;
  }, []);

  const isCompleted = !!state?.completed_at;
  const progressPct = state?.progress_pct ?? 0;
  const currentStepId = state?.current_step;
  const currentStep = useMemo(
    () => steps.find((s) => s.id === currentStepId) || null,
    [steps, currentStepId]
  );

  const value = useMemo(() => ({
    state, steps, accountType, loading, error,
    isEligible, isCompleted, progressPct, currentStep, currentStepId,
    refresh, complete, skip, mark, reset,
  }), [state, steps, accountType, loading, error, isEligible, isCompleted, progressPct, currentStep, currentStepId, refresh, complete, skip, mark, reset]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOnboarding() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useOnboarding must be used inside OnboardingProvider");
  return ctx;
}
